/** City centers for Eastern Province fallbacks when a property has no map URL coords. */
export const EASTERN_CITY_CENTERS = {
  Khobar: { lat: 26.2172, lng: 50.1971 },
  Dammam: { lat: 26.4207, lng: 50.0888 },
  Dhahran: { lat: 26.2361, lng: 50.0393 },
  Jubail: { lat: 27.0046, lng: 49.646 },
  Qatif: { lat: 26.5652, lng: 49.9944 },
  Khafji: { lat: 28.4391, lng: 48.4912 },
  "Ras Tanura": { lat: 26.6439, lng: 50.1583 },
  Abqaiq: { lat: 25.9336, lng: 49.6686 },
  "Hofuf (Al-Ahsa)": { lat: 25.3647, lng: 49.5869 },
  "Hafar al-Batin": { lat: 28.4342, lng: 45.9636 },
};

export const DEFAULT_MAP_CENTER = { lat: 26.35, lng: 50.1 };
export const DEFAULT_MAP_ZOOM = 10;

const CITY_ALIASES = [
  ["khobar", "Khobar"],
  ["al khobar", "Khobar"],
  ["al-khobar", "Khobar"],
  ["alkhobar", "Khobar"],
  // Bad auto-translate of Arabic "الخبر" (Al-Khobar)
  ["the news", "Khobar"],
  ["الخبر", "Khobar"],
  ["dammam", "Dammam"],
  ["الدمام", "Dammam"],
  ["dhahran", "Dhahran"],
  ["الظهران", "Dhahran"],
  ["jubail", "Jubail"],
  ["الجبيل", "Jubail"],
  ["qatif", "Qatif"],
  ["القطيف", "Qatif"],
  ["khafji", "Khafji"],
  ["الخفجي", "Khafji"],
  ["ras tanura", "Ras Tanura"],
  ["رأس تنورة", "Ras Tanura"],
  ["abqaiq", "Abqaiq"],
  ["بقيق", "Abqaiq"],
  ["hofuf", "Hofuf (Al-Ahsa)"],
  ["al-ahsa", "Hofuf (Al-Ahsa)"],
  ["alahsa", "Hofuf (Al-Ahsa)"],
  ["الهفوف", "Hofuf (Al-Ahsa)"],
  ["hafr", "Hafar al-Batin"],
  ["hafar", "Hafar al-Batin"],
  ["حفر الباطن", "Hafar al-Batin"],
];

/** Neighborhood / district hints when city is missing or mistranslated. */
const AREA_CITY_HINTS = [
  ["pearl", "Khobar"],
  ["corniche", "Khobar"],
  ["tahlia", "Khobar"],
  ["taawun", "Khobar"],
  ["olaya", "Khobar"],
  ["hamra", "Khobar"],
  ["buhaira", "Khobar"],
  ["bandar", "Dammam"],
  ["faisaliyah", "Dammam"],
  ["shati", "Dammam"],
  ["fanateer", "Jubail"],
];

export function normalizeCityKey(value = "") {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return "";
  for (const [alias, key] of CITY_ALIASES) {
    if (text.includes(alias)) return key;
  }
  for (const [hint, key] of AREA_CITY_HINTS) {
    if (text.includes(hint)) return key;
  }
  const exact = Object.keys(EASTERN_CITY_CENTERS).find((city) => city.toLowerCase() === text);
  return exact || String(value || "").trim();
}

export function extractCoordsFromMapUrl(url = "") {
  const value = String(url || "").trim();
  if (!value) return null;

  const patterns = [
    /[?&]q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i,
    /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
    /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
    /ll=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i,
    /center=(-?\d+(?:\.\d+)?)%2C(-?\d+(?:\.\d+)?)/i,
    /center=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i,
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (!match) continue;
    const lat = Number(match[1]);
    const lng = Number(match[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      return { lat, lng, source: "map" };
    }
  }

  return null;
}

function stableJitter(seed = "") {
  let hash = 0;
  const text = String(seed);
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash << 5) - hash + text.charCodeAt(i);
    hash |= 0;
  }
  const a = ((hash % 1000) / 1000) * 0.018 - 0.009;
  const b = ((((hash / 1000) | 0) % 1000) / 1000) * 0.018 - 0.009;
  return { lat: a, lng: b };
}

export function resolvePropertyMapPoint(property = {}) {
  const fromMap = extractCoordsFromMapUrl(property.mapEmbedUrl || property.mapUrl || "");
  if (fromMap) {
    return {
      lat: fromMap.lat,
      lng: fromMap.lng,
      precise: true,
      cityKey: normalizeCityKey(
        property.city || property.area || property.location || property.title || ""
      ),
    };
  }

  const cityKey = normalizeCityKey(
    property.city || property.area || property.location || property.title || ""
  );
  const center = EASTERN_CITY_CENTERS[cityKey];
  if (!center) return null;

  const jitter = stableJitter(property.id || property.slug || property.title || cityKey);
  return {
    lat: center.lat + jitter.lat,
    lng: center.lng + jitter.lng,
    precise: false,
    cityKey,
  };
}
